'use client';

import { useState, useEffect, use } from 'react';
import { useRouter } from 'next/navigation';
import { getLocationById, updateLocation, deleteLocation, askQwen } from '@/lib/actions';
import { ArrowLeft, Sparkles, Save, Trash2, Wand2, Plus, X } from 'lucide-react';
import Link from 'next/link';

export default function EditLocationPage({ params }: { params: Promise<{ id: string }> }) {
  const { id } = use(params);
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [brainDump, setBrainDump] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    lore: '',
    custom_fields: '[]'
  });

  const [customFields, setCustomFields] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    async function load() {
      const loc = await getLocationById(id);
      if (loc) {
        setFormData({
          name: loc.name || '',
          type: loc.type || '',
          lore: loc.lore || '',
          custom_fields: loc.custom_fields || '[]'
        });
        try {
          setCustomFields(JSON.parse(loc.custom_fields || '[]'));
        } catch (e) {
          setCustomFields([]);
        }
      }
    }
    load();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCustomChange = (index: number, field: 'label' | 'value', val: string) => {
    const newFields = [...customFields];
    newFields[index][field] = val;
    setCustomFields(newFields);
  };

  const addCustomField = () => {
    setCustomFields([...customFields, { label: '', value: '' }]);
  };

  const removeCustomField = (index: number) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (k !== 'custom_fields') data.append(k, v);
    });
    data.append('custom_fields', JSON.stringify(customFields));

    try {
      await updateLocation(id, data);
      router.push(`/locations/${id}`);
      router.refresh();
    } catch (err) {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure?")) return;
    setIsDeleting(true);
    try {
      await deleteLocation(id);
      router.push('/locations');
      router.refresh();
    } catch (err) {
      setIsDeleting(false);
    }
  };

  const handleBrainDump = async () => {
    if (!brainDump.trim()) return;
    setIsParsing(true);
    try {
      const prompt = `Update this location: "${brainDump}". Current: ${JSON.stringify(formData)}. Return ONLY JSON.`;
      const response = await askQwen(prompt, "You update location JSON.");
      const jsonStr = response.match(/\{[\s\S]*\}/)?.[0];
      if (jsonStr) {
        setFormData(prev => ({ ...prev, ...JSON.parse(jsonStr) }));
      }
    } catch (err) {} finally {
      setIsParsing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col p-8">
      <header className="flex justify-between items-center mb-12">
        <Link href={`/locations/${id}`} className="flex items-center gap-2 text-slate-400"><ArrowLeft size={20} /> Cancel</Link>
        <button onClick={handleDelete} disabled={isDeleting} className="text-red-400 hover:text-red-300 flex items-center gap-2 text-sm font-bold bg-red-950/30 px-4 py-2 rounded-lg border border-red-900/50">
           <Trash2 size={16} /> {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      </header>
      <main className="max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12">
        <section className="space-y-6">
           <h2 className="text-2xl font-serif font-bold text-blue-400 italic flex items-center gap-2"><Sparkles /> Refine the Realm</h2>
           <textarea value={brainDump} onChange={(e) => setBrainDump(e.target.value)} placeholder="New details about this place..." className="w-full h-32 bg-slate-900 border border-slate-800 rounded-xl p-4 text-sm" />
           <button onClick={handleBrainDump} disabled={isParsing} className="w-full bg-blue-600 py-3 rounded-xl font-bold flex items-center justify-center gap-2">
             {isParsing ? 'Oracle is thinking...' : 'Update with AI'}
           </button>
           <div className="pt-8">
             <div className="flex justify-between mb-4">
                <h3 className="text-lg font-serif font-bold italic text-slate-300">Custom Details</h3>
                <button onClick={addCustomField} className="text-xs text-blue-400 flex items-center gap-1"><Plus size={14} /> Add Category</button>
             </div>
             <div className="space-y-4">
                {customFields.map((field, i) => (
                  <div key={i} className="flex gap-2 items-start bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                     <div className="flex-1 space-y-2">
                        <input value={field.label} onChange={(e) => handleCustomChange(i, 'label', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-1 text-xs text-blue-300 font-bold uppercase" />
                        <textarea value={field.value} onChange={(e) => handleCustomChange(i, 'value', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm" />
                     </div>
                     <button onClick={() => removeCustomField(i)} className="text-slate-600 hover:text-red-400"><X size={16} /></button>
                  </div>
                ))}
             </div>
           </div>
        </section>
        <form onSubmit={handleSubmit} className="space-y-6 bg-slate-900/50 p-8 rounded-3xl border border-slate-800">
           <input required name="name" value={formData.name} onChange={handleChange} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
           <input name="type" value={formData.type} onChange={handleChange} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
           <textarea name="lore" value={formData.lore} onChange={handleChange} rows={6} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4" />
           <button type="submit" disabled={loading} className="w-full bg-emerald-600 py-4 rounded-xl font-bold flex items-center justify-center gap-2"><Save /> {loading ? 'Saving...' : 'Save Changes'}</button>
        </form>
      </main>
    </div>
  );
}
